/**
 * Modal reusable de confirmación para mutations admin (BO-5 bloque H).
 *
 * Patrón:
 *   - Single confirm para recoverables (pause job, snooze, resume).
 *   - Confirm + reason (textarea required) para mutativas (suspend,
 *     issue-credits, retry-failed, drain).
 *   - Confirm + texto literal "CONFIRMAR" para destructivas (suspend,
 *     reset-password) via prop `requiresLiteralConfirm`.
 */
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';

export interface MutationModalProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'primary' | 'warn' | 'danger';
  requiresReason?: boolean;
  reasonPlaceholder?: string;
  /** Si presente, exige que el user escriba este texto literal para activar el botón. */
  requiresLiteralConfirm?: string;
  isPending?: boolean;
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  children?: ReactNode;
}

export function MutationModal(props: MutationModalProps): ReactElement | null {
  const [reason, setReason] = useState('');
  const [literal, setLiteral] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (props.open) {
      setReason('');
      setLiteral('');
      // Focus al textarea / input cuando abre.
      const t = setTimeout(() => reasonRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [props.open]);

  if (!props.open) return null;

  const reasonOk = !props.requiresReason || reason.trim().length > 0;
  const literalOk =
    !props.requiresLiteralConfirm || literal.trim() === props.requiresLiteralConfirm;
  const canConfirm = reasonOk && literalOk && !props.isPending;
  const tone = props.tone ?? 'primary';
  const confirmClass =
    tone === 'danger' ? 'btn-danger' : tone === 'warn' ? 'btn-warn' : 'btn-primary';

  return (
    <div
      className="mm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onCancel();
      }}
      data-testid="mutation-modal"
    >
      <div className="mm-dialog" role="dialog" aria-modal="true" aria-labelledby="mm-title">
        <header className="mm-head">
          <h3 id="mm-title" className="mm-title">
            {props.title}
          </h3>
        </header>
        <div className="mm-body">
          {props.description ? <div className="mm-desc">{props.description}</div> : null}
          {props.children}
          {props.requiresReason ? (
            <label className="mm-field">
              <span className="mm-label">Motivo</span>
              <textarea
                ref={reasonRef}
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={props.reasonPlaceholder ?? 'Motivo de la acción…'}
                data-testid="mutation-modal-reason"
              />
            </label>
          ) : null}
          {props.requiresLiteralConfirm ? (
            <label className="mm-field">
              <span className="mm-label">
                Escribí <code>{props.requiresLiteralConfirm}</code> para confirmar
              </span>
              <input
                type="text"
                value={literal}
                onChange={(e) => setLiteral(e.target.value)}
                data-testid="mutation-modal-literal"
              />
            </label>
          ) : null}
          {props.errorMessage ? (
            <div className="mm-error" role="alert">
              {props.errorMessage}
            </div>
          ) : null}
        </div>
        <footer className="mm-foot">
          <button
            type="button"
            className="btn"
            onClick={props.onCancel}
            disabled={props.isPending}
            data-testid="mutation-modal-cancel"
          >
            {props.cancelLabel ?? 'Cancelar'}
          </button>
          <button
            type="button"
            className={confirmClass}
            onClick={() => {
              if (canConfirm) props.onConfirm(reason.trim());
            }}
            disabled={!canConfirm}
            data-testid="mutation-modal-confirm"
          >
            {props.isPending ? 'Procesando…' : props.confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
